import { Inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { getBrowserLang } from '@ngneat/transloco';
import { Subscription } from 'rxjs';

import { Logger } from '@shared';
import { DOCUMENT } from '@angular/common';
import * as Sentry from '@sentry/angular-ivy';

const log = new Logger('I18nService');
const languageKey = 'language';

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  defaultLanguage!: string;
  supportedLanguages!: string[];

  private langChangeSubscription!: Subscription;

  constructor(
    private translocoService: TranslocoService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // Embed languages to avoid extra HTTP requests
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */
  init(defaultLanguage: string, supportedLanguages: string[]) {
    this.defaultLanguage = defaultLanguage;
    this.supportedLanguages = supportedLanguages;
    this.language = '';
    // Warning: this subscription will always be alive for the app's lifetime
    this.langChangeSubscription = this.translocoService.langChanges$.subscribe((lang) => {
      localStorage.setItem(languageKey, lang);
      this.document.documentElement.lang = lang.split('-')[0];
    });
  }

  /**
   * Cleans up language change subscription.
   */
  destroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  set language(language: string) {
    let newLanguage =
      language || localStorage.getItem(languageKey) || (getBrowserLang() != undefined ? getBrowserLang() : '') || '';
    let isSupportedLanguage = this.supportedLanguages.includes(newLanguage);

    // If no exact match is found, search without the region
    if (newLanguage && !isSupportedLanguage) {
      newLanguage = newLanguage.split('-')[0];
      newLanguage =
        this.supportedLanguages.find((supportedLanguage) => supportedLanguage.startsWith(newLanguage)) || '';
      isSupportedLanguage = Boolean(newLanguage);
    }

    // Fallback if language is not supported
    if (!newLanguage || !isSupportedLanguage) {
      newLanguage = this.defaultLanguage;
    }

    language = newLanguage;

    log.debug(`Language set to ${language}`);
    Sentry.setTag('page_locale', language);
    this.translocoService.load(language).subscribe({
      next: (value) => {
        localStorage.setItem(languageKey, language);
        this.translocoService.setActiveLang(language);
      },
    });
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  get language(): string {
    return this.translocoService.getActiveLang();
  }
}
